/**
 * services/adaptive/exposureIntelligence.ts
 *
 * Exposure Intelligence — MER Algo Footprints v7.1B
 *
 * Provides observational concentration / overlap intelligence
 * across the active idea set (open audit records + saved trade plans).
 *
 * Informational only. NOT portfolio execution control.
 * NOT position sizing automation. NOT advisory.
 *
 * Non-executing. Decision support only.
 */

import type { AuditRecord } from '../../../../types';
import type { TradePlan }   from '../../../../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExposureBreakdown {
  longCount:    number;
  shortCount:   number;
  bySetupType:  Record<string, number>;
  byRegime:     Record<string, number>;
  byPhase:      Record<string, number>;
}

export interface ExposureWarning {
  type:     'directional_concentration' | 'setup_clustering' | 'regime_clustering' | 'phase_clustering' | 'insufficient_diversity';
  severity: 'low' | 'medium' | 'high';
  message:  string;
}

export interface ExposureIntelligenceSummary {
  activeIdeas:          number;
  breakdown:            ExposureBreakdown;
  diversificationScore: number | null;   // 0–100
  warnings:             ExposureWarning[];
  summary:              string;
}

// ─── Concentration thresholds ─────────────────────────────────────────────────
// All purely informational — not enforced, not execution control.

const WARN_DIR_HIGH    = 0.80;   // 80% one direction → high severity
const WARN_DIR_MED     = 0.65;   // 65% one direction → medium severity
const WARN_CLUSTER_HIGH = 0.60;  // 60% same setup/regime/phase → high
const WARN_CLUSTER_MED  = 0.45;  // 45% → medium

// ─── Helpers ──────────────────────────────────────────────────────────────────

function countByKey<T extends Record<string, any>>(
  items: T[],
  key: keyof T,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const item of items) {
    const k = String(item[key] ?? 'unknown');
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}

function topConcentration(counts: Record<string, number>, total: number): number {
  if (!total) return 0;
  const max = Math.max(...Object.values(counts));
  return max / total;
}

/** Simple normalized Shannon entropy as a diversity proxy (0 = no diversity, 1 = max). */
function shannonDiversity(counts: Record<string, number>, total: number): number {
  if (!total) return 0;
  let entropy = 0;
  for (const n of Object.values(counts)) {
    if (n > 0) {
      const p = n / total;
      entropy -= p * Math.log2(p);
    }
  }
  const maxEntropy = Math.log2(Object.keys(counts).length || 1);
  return maxEntropy > 0 ? entropy / maxEntropy : 0;
}

function buildWarnings(
  breakdown: ExposureBreakdown,
  total: number,
): ExposureWarning[] {
  const warnings: ExposureWarning[] = [];
  if (total === 0) return warnings;

  // Directional
  const longPct  = breakdown.longCount  / total;
  const shortPct = breakdown.shortCount / total;
  const maxDir   = Math.max(longPct, shortPct);
  const side     = longPct >= shortPct ? 'long' : 'short';
  if (maxDir >= WARN_DIR_HIGH) {
    warnings.push({
      type:     'directional_concentration',
      severity: 'high',
      message:  `${(maxDir * 100).toFixed(0)}% of active ideas are ${side}-biased. Directional concentration is high.`,
    });
  } else if (maxDir >= WARN_DIR_MED) {
    warnings.push({
      type:     'directional_concentration',
      severity: 'medium',
      message:  `${(maxDir * 100).toFixed(0)}% of active ideas are ${side}-biased.`,
    });
  }

  // Setup clustering
  const setupConc = topConcentration(breakdown.bySetupType, total);
  const topSetup  = Object.entries(breakdown.bySetupType).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  if (setupConc >= WARN_CLUSTER_HIGH) {
    warnings.push({
      type: 'setup_clustering', severity: 'high',
      message: `${(setupConc * 100).toFixed(0)}% of ideas are "${topSetup}". Setup clustering is high.`,
    });
  } else if (setupConc >= WARN_CLUSTER_MED) {
    warnings.push({
      type: 'setup_clustering', severity: 'medium',
      message: `${(setupConc * 100).toFixed(0)}% of ideas are "${topSetup}".`,
    });
  }

  // Regime clustering
  const regimeConc = topConcentration(breakdown.byRegime, total);
  const topRegime  = Object.entries(breakdown.byRegime).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  if (regimeConc >= WARN_CLUSTER_HIGH) {
    warnings.push({
      type: 'regime_clustering', severity: 'high',
      message: `${(regimeConc * 100).toFixed(0)}% of ideas are in "${topRegime}" regime.`,
    });
  } else if (regimeConc >= WARN_CLUSTER_MED) {
    warnings.push({
      type: 'regime_clustering', severity: 'medium',
      message: `${(regimeConc * 100).toFixed(0)}% of ideas are in "${topRegime}" regime.`,
    });
  }

  // Phase clustering
  const phaseConc = topConcentration(breakdown.byPhase, total);
  const topPhase  = Object.entries(breakdown.byPhase).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  if (phaseConc >= WARN_CLUSTER_HIGH) {
    warnings.push({
      type: 'phase_clustering', severity: 'high',
      message: `${(phaseConc * 100).toFixed(0)}% of ideas are in "${topPhase}" phase.`,
    });
  }

  // Insufficient diversity
  if (total >= 3 && Object.keys(breakdown.bySetupType).length === 1 &&
      Object.keys(breakdown.byRegime).length === 1) {
    warnings.push({
      type: 'insufficient_diversity', severity: 'medium',
      message: 'All active ideas share the same setup type and regime — limited contextual diversity.',
    });
  }

  return warnings;
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function computeExposureIntelligence(
  auditRecords: AuditRecord[],
  tradePlans:   TradePlan[],
): ExposureIntelligenceSummary {

  // Active = open audit records + planned trade plans (not expired/invalidated/skipped)
  type Idea = { direction: string; setupType: string; regime: string; phase: string };

  const fromAudit: Idea[] = auditRecords
    .filter(r => r.status === 'open')
    .map(r => ({
      direction: r.direction,
      setupType: r.setupType,
      regime:    r.regime,
      phase:     r.phase,
    }));

  const fromPlans: Idea[] = tradePlans
    .filter(p => p.status === 'planned' || p.status === 'taken')
    .map(p => ({
      direction: p.direction,
      setupType: p.setupType,
      regime:    p.regime,
      phase:     p.phase,
    }));

  // Deduplicate by identity (same setup+regime+phase+direction counts as one idea slot)
  const allIdeas = [...fromAudit, ...fromPlans];
  const total = allIdeas.length;

  const breakdown: ExposureBreakdown = {
    longCount:   allIdeas.filter(i => i.direction === 'long').length,
    shortCount:  allIdeas.filter(i => i.direction === 'short').length,
    bySetupType: countByKey(allIdeas, 'setupType'),
    byRegime:    countByKey(allIdeas, 'regime'),
    byPhase:     countByKey(allIdeas, 'phase'),
  };

  // Diversification score: combine setup, regime, and directional diversity
  if (total === 0) {
    return {
      activeIdeas:          0,
      breakdown,
      diversificationScore: null,
      warnings:             [],
      summary:              'No active audit records or trade plans found.',
    };
  }

  const setupDiv  = shannonDiversity(breakdown.bySetupType, total);
  const regimeDiv = shannonDiversity(breakdown.byRegime, total);
  const dirBalance = 1 - Math.abs(breakdown.longCount - breakdown.shortCount) / total;
  // Weighted composite: setup diversity is most important
  const rawScore = setupDiv * 0.45 + regimeDiv * 0.35 + dirBalance * 0.20;
  const diversificationScore = Math.round(rawScore * 100);

  const warnings = buildWarnings(breakdown, total);

  const highWarn = warnings.filter(w => w.severity === 'high').length;
  const summary = total === 0 ? 'No active ideas.'
    : highWarn > 0
    ? `${total} active idea${total > 1 ? 's' : ''} — ${highWarn} high-severity concentration warning${highWarn > 1 ? 's' : ''} detected.`
    : warnings.length > 0
    ? `${total} active idea${total > 1 ? 's' : ''} — moderate concentration noted. Review breakdown.`
    : `${total} active idea${total > 1 ? 's' : ''} — reasonable diversification (score ${diversificationScore}/100).`;

  return { activeIdeas: total, breakdown, diversificationScore, warnings, summary };
}
